import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ordersAPI } from '../../services/api';
import { textures } from '../../data/products';
import '../admin/AdminComponents.css';

const OrdersManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [regenerating, setRegenerating] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    const orderId = searchParams.get('orderId');
    if (orderId && orders.length > 0) {
      const order = orders.find(o => o.id === parseInt(orderId) || o.order_id === orderId);
      if (order) {
        handleViewOrder(order);
      }
    }
  }, [searchParams, orders]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await ordersAPI.getAll();
      if (response.success) {
        setOrders(response.data || []);
      } else {
        setError(response.message || 'Failed to load orders');
      }
    } catch (err) {
      console.error('Error loading orders:', err);
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const response = await ordersAPI.updateStatus(orderId, newStatus);
      if (response.success) {
        await loadOrders();
        if (selectedOrder && selectedOrder.id === orderId) {
          setSelectedOrder(response.data);
        }
      } else {
        alert(response.message || 'Failed to update order status');
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      alert(err.message || 'Failed to update order status');
    }
  };

  const handleViewOrder = async (order) => {
    try {
      setLoadingOrderDetails(true);
      const response = await ordersAPI.getById(order.id);
      if (response.success && response.data) {
        setSelectedOrder(response.data);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('orderId', order.id);
        setSearchParams(newSearchParams, { replace: true });
      } else {
        setSelectedOrder(order);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('orderId', order.id);
        setSearchParams(newSearchParams, { replace: true });
      }
    } catch (err) {
      console.error('Error loading order details:', err);
      setSelectedOrder(order);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('orderId', order.id);
      setSearchParams(newSearchParams, { replace: true });
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const handleCloseOrderModal = () => {
    setSelectedOrder(null);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('orderId');
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleRegenerateDownload = async (orderId) => {
    if (!window.confirm('This will delete the old download link and create a new one. Continue?')) {
      return;
    }

    try {
      setRegenerating(true);
      const response = await ordersAPI.regenerateDownloadAdmin(orderId);
      if (response.success) {
        await loadOrders();
        const detailResponse = await ordersAPI.getById(orderId);
        if (detailResponse.success && detailResponse.data) {
          setSelectedOrder(detailResponse.data);
        }
        alert('Download link regenerated successfully!');
      } else {
        alert(response.message || 'Failed to regenerate download link');
      }
    } catch (err) {
      console.error('Error regenerating download link:', err);
      alert(err.message || 'Failed to regenerate download link');
    } finally {
      setRegenerating(false);
    }
  };

  const handleDownloadOrderZip = async (orderId) => {
    try {
      setDownloadingZip(true);
      const { blob, filename } = await ordersAPI.downloadOrderZipAdmin(orderId);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading order ZIP:', error);
      alert(error.message || 'Failed to download order ZIP');
    } finally {
      setDownloadingZip(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
      case 'paid':
        return '#4caf50';
      case 'processing':
        return '#ff9800';
      case 'shipped':
        return '#2196f3';
      case 'delivered':
        return '#4caf50';
      case 'cancelled':
        return '#f44336';
      default:
        return '#8b0000';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredOrders = statusFilter === 'all'
    ? orders
    : orders.filter(order => order.status === statusFilter);

  if (loading) {
    return (
      <div className="admin-section">
        <div className="loading-message">Loading orders...</div>
      </div>
    );
  }
  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Orders Management</h2>
        <div className="admin-section-actions">
          <select
            className="admin-form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ width: 'auto', minWidth: '150px' }}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="paid">Paid</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>

            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>

          </select>
          <button className="admin-button" onClick={loadOrders}>
            Refresh
          </button>
        </div>
      </div>
      {error && (
        <div className="admin-error-message">{error}</div>
      )}
      {filteredOrders.length === 0 ? (
        <div className="admin-empty-state">
          <p>No orders found{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.</p>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Email</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <button
                      className="admin-link-button"
                      onClick={() => handleViewOrder(order)}
                      disabled={loadingOrderDetails}
                    >
                      {order.order_id}
                    </button>
                  </td>
                  <td>{order.customer_first_name} {order.customer_last_name}</td>
                  <td>{order.customer_email}</td>
                  <td>{order.items?.length || 0} item(s)</td>
                  <td>${(parseFloat(order.total) || 0).toFixed(2)}</td>
                  <td>
                    <span
                      className="admin-status-badge"
                      style={{ color: getStatusColor(order.status) }}
                    >
                      {order.status?.charAt(0).toUpperCase() + order.status?.slice(1) || 'Unknown'}
                    </span>
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>
                    <button
                      className="admin-button small"
                      onClick={() => handleViewOrder(order)}
                      disabled={loadingOrderDetails}
                    >
                      {loadingOrderDetails ? 'Loading...' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selectedOrder && (
        <div className="admin-modal-overlay" onClick={handleCloseOrderModal}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">Order Details - {selectedOrder.order_id}</h3>
              <button className="admin-modal-close" onClick={handleCloseOrderModal}>×</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label className="admin-form-label">Order Information</label>
                <div className="admin-info-grid">
                  <div>
                    <strong>Order ID:</strong> {selectedOrder.order_id}
                  </div>
                  <div>
                    <strong>Status:</strong>{' '}
                    <span style={{ color: getStatusColor(selectedOrder.status) }}>
                      {selectedOrder.status?.charAt(0).toUpperCase() + selectedOrder.status?.slice(1) || 'Unknown'}
                    </span>
                  </div>
                  <div>
                    <strong>Date:</strong> {formatDate(selectedOrder.created_at)}
                  </div>
                  <div>
                    <strong>Total:</strong> ${(parseFloat(selectedOrder.total) || 0).toFixed(2)}

                  </div>
                </div>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Customer Information</label>
                <div className="admin-info-grid">
                  <div>
                    <strong>Name:</strong> {selectedOrder.customer_first_name} {selectedOrder.customer_last_name}
                  </div>
                  <div>
                    <strong>Email:</strong> {selectedOrder.customer_email}
                  </div>
                  {selectedOrder.customer_phone && (
                    <div>
                      <strong>Phone:</strong> {selectedOrder.customer_phone}
                    </div>
                  )}
                  <div>
                    <strong>Address:</strong> {selectedOrder.customer_address}
                  </div>
                  <div>
                    <strong>City:</strong> {selectedOrder.customer_city}
                  </div>
                </div>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Order Items</label>
                <div className="admin-order-items">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="admin-order-item">
                      <div className="admin-order-item-image">
                        {item.productImage && (
                          <img src={item.productImage} alt={item.productName} />
                        )}
                      </div>
                      <div className="admin-order-item-details">
                        <div className="admin-order-item-name">{item.productName}</div>
                        <div className="admin-order-item-meta">
                          {item.texture && (
                            <span>Texture: {textures[item.texture]?.name || item.texture}</span>
                          )}
                          <span>Quantity: {item.quantity}</span>
                          <span>Price: ${(parseFloat(item.price) || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="admin-order-item-total">
                        ${((parseFloat(item.price) || 0) * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Update Status</label>
                <select
                  className="admin-form-select"
                  value={selectedOrder.status || 'pending'}
                  onChange={(e) => handleStatusUpdate(selectedOrder.id, e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="paid">Paid</option>
                  <option value="processing">Processing</option>

                  <option value="shipped">Shipped</option>

                  <option value="delivered">Delivered</option>

                  <option value="cancelled">Cancelled</option>

                </select>

              </div>


              {}
              {(
                ['paid', 'shipped', 'delivered'].includes((selectedOrder.status || '').toLowerCase()) ||
                selectedOrder.downloadToken ||
                selectedOrder.download_token
              ) && (
                <div className="admin-form-group">
                  <label className="admin-form-label">Download Link</label>

                  {(selectedOrder.downloadToken || selectedOrder.download_token) ? (
                    <>
                      <div className="admin-info-grid">
                        <div>
                          <strong>Download URL:</strong>

                          <div style={{ marginTop: '0.5rem', wordBreak: 'break-all' }}>
                            <a
                              href={selectedOrder.downloadUrl || `${window.location.origin}/api/downloads/${selectedOrder.downloadToken || selectedOrder.download_token}/file`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: (selectedOrder.downloadExpired || new Date(selectedOrder.downloadExpiresAt || selectedOrder.download_expires_at) < new Date())
                                  ? '#ff9800'
                                  : 'var(--accent)',
                                textDecoration: 'underline'
                              }}
                            >
                              {selectedOrder.downloadUrl || `${window.location.origin}/api/downloads/${selectedOrder.downloadToken || selectedOrder.download_token}/file`}
                            </a>

                            {(selectedOrder.downloadExpired || (selectedOrder.downloadExpiresAt && new Date(selectedOrder.downloadExpiresAt) < new Date())) && (
                              <span style={{ color: '#ff9800', marginLeft: '0.5rem', fontSize: '0.9rem' }}>(Expired)</span>

                            )}
                          </div>

                        </div>

                        <div>
                          <strong>Password:</strong>

                          <div style={{ marginTop: '0.5rem', fontFamily: 'monospace' }}>
                            {selectedOrder.downloadPassword || selectedOrder.download_password || 'N/A'}
                          </div>

                        </div>

                        <div>
                          <strong>Expires At:</strong>

                          <div style={{ marginTop: '0.5rem' }}>
                            {selectedOrder.downloadExpiresAt
                              ? formatDate(selectedOrder.downloadExpiresAt)
                              : 'N/A'}
                            {(selectedOrder.downloadExpiresAt && new Date(selectedOrder.downloadExpiresAt) < new Date()) && (
                              <span style={{ color: '#ff9800', marginLeft: '0.5rem' }}>(Expired)</span>

                            )}
                          </div>

                        </div>

                      </div>

                    </>

                  ) : (
                    <div style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                      No download link exists yet. Click "Regenerate Download Link" to create one.
                    </div>

                  )}
                  <div className="admin-form-actions" style={{ marginTop: '1rem', justifyContent: 'flex-start' }}>
                    <button
                      className="admin-button"
                      onClick={() => handleRegenerateDownload(selectedOrder.id)}
                      disabled={regenerating}
                    >
                      {regenerating ? 'Regenerating...' : 'Regenerate Download Link'}
                    </button>
                    <button
                      className="admin-button primary"
                      onClick={() => handleDownloadOrderZip(selectedOrder.id)}
                      disabled={downloadingZip}
                    >
                      {downloadingZip ? 'Preparing ZIP...' : 'Download Order ZIP'}
                    </button>
                  </div>

                </div>

              )}

              <div className="admin-form-actions">
                <button
                  className="admin-button"
                  onClick={handleCloseOrderModal}
                >
                  Close
                </button>

              </div>

            </div>

          </div>

        </div>

      )}
    </div>

  );
};

export default OrdersManagement;
